import { isEqual } from 'lodash-es';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import { EMPTY_PCD_DATA } from '@/constant/pcdConstant';
import type {
  PCDData,
  PcdCacheStoreActions,
  PcdCacheStoreState,
  PcdFileCacheStatus,
} from '@/type';
import appWorker from '@/worker';

import useTenantStore from '../tenantStore';
import { parsePcdPathAsync, setIDBVersion } from './util';

const INIT_STATE: PcdCacheStoreState = {
  get version() {
    return import.meta.env.VITE_APP_PCD_CACHE_VERSION;
  },
  cachedPcdMapByPath: {},
  cacheStatus: {},
};

let CURRENT_STORED_VERSION = (await appWorker.request('idb', 'get', {
  key: 'value',
  storeName: 'version',
})) as Nullish<string>;

const usePcdCacheStore = createWithEqualityFn<
  PcdCacheStoreState & PcdCacheStoreActions
>((set, get) => {
  CURRENT_STORED_VERSION !== INIT_STATE.version &&
    setIDBVersion(INIT_STATE.version).then(
      () => (CURRENT_STORED_VERSION = INIT_STATE.version),
    );

  const assert = () => {
    const tenantID = getTenantID();
    let errorMessage = '';

    if (!tenantID) errorMessage = 'Empty tenantID. Please retry sign in';

    if (errorMessage) return new Error(errorMessage);

    return tenantID!;
  };

  const getTenantID = () =>
    useTenantStore.getState().getCurrentTenant()?.tenantID;

  return {
    ...INIT_STATE,

    async addPcdPathList(pcdDirPath, pcdNameEntry) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      const cachedPcdMap =
        get().cachedPcdMapByPath[pcdDirPath] ??
        (await get().getPcdPathMap(pcdDirPath));

      pcdNameEntry = pcdNameEntry.filter(
        ([pcdIndex]) => !cachedPcdMap[pcdIndex],
      );

      if (!pcdNameEntry.length) return Promise.resolve();

      pcdNameEntry.forEach(([pcdIndex, pcdName]) => {
        cachedPcdMap[pcdIndex] = pcdName;
      });

      return get().setPcdPathMap(pcdDirPath, cachedPcdMap);
    },

    clear() {
      set({ ...INIT_STATE });
    },

    clearPcdList(pcdDirPath) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      return appWorker
        .request('idb', 'clearStore', {
          storeName: `${tenantID}_${pcdDirPath}_cached`,
        })
        .catch(error => {
          console.group('[usePcdCacheStore.clearPcdList] Error');
          console.warn(error);
          console.groupEnd();

          return Promise.resolve();
        });
    },

    clearPcdPathMap(pcdDirPath) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      return appWorker
        .request('idb', 'clearStore', {
          storeName: `${tenantID}_${pcdDirPath}_list`,
        })
        .catch(error => {
          console.group('[usePcdCacheStore.clearPcdPathMap] Error');
          console.warn(error);
          console.groupEnd();

          return Promise.resolve();
        });
    },

    clearPcdStatusMap(pcdDirPath) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      return appWorker
        .request('idb', 'clearStore', {
          storeName: `${tenantID}_${pcdDirPath}_status`,
        })
        .then(() => {
          const newCacheStatus = { ...get().cacheStatus };
          delete newCacheStatus[pcdDirPath];

          set({ cacheStatus: newCacheStatus });
        })
        .catch(error => {
          console.group('[usePcdCacheStore.clearPcdStatusMap] Error');
          console.warn(error);
          console.groupEnd();

          return Promise.resolve();
        });
    },

    getPcd(pcdPath) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      return parsePcdPathAsync(pcdPath)
        .then(
          ({ fileName, dirPath }) =>
            appWorker.request('idb', 'get', {
              key: fileName!,
              storeName: `${tenantID}_${dirPath}_cached`,
            }) as Promise<Nullish<PCDData>>,
        )
        .then(pcdData => pcdData ?? EMPTY_PCD_DATA)
        .catch(error => {
          console.group('[usePcdCacheStore.getPcd] Error');
          console.warn(error);
          console.groupEnd();

          return EMPTY_PCD_DATA;
        });
    },

    getPcdList(pathList) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      const [pcdPath] = pathList;

      return parsePcdPathAsync(pcdPath)
        .then(
          ({ dirPath }) =>
            appWorker.request('idb', 'getList', {
              keys: pathList.map(p => p.replace(`${dirPath}/`, '')),
              storeName: `${tenantID}_${dirPath}_cached`,
            }) as Promise<Nullish<PCDData[]>>,
        )
        .catch(error => {
          console.group('[usePcdCacheStore.getPcdList] Error');
          console.warn(error);
          console.groupEnd();

          return Array.from({ length: pathList.length }, () => EMPTY_PCD_DATA);
        });
    },

    getPcdPathMap(pcdDirPath) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      return appWorker
        .request('idb', 'get', {
          key: 'value',
          storeName: `${tenantID}_${pcdDirPath}_list`,
        })
        .then(
          value =>
            (value as Nullish<Dictionary<string>>) ??
            ({} as Dictionary<string>),
        )
        .then(value => {
          set({
            cachedPcdMapByPath: {
              ...get().cachedPcdMapByPath,
              [pcdDirPath]: value,
            },
          });

          return value;
        })
        .catch(error => {
          console.group('[usePcdCacheStore.getPcdPathList] Error');
          console.warn(error);
          console.groupEnd();

          return {};
        });
    },

    getPcdStatus(pcdPath) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      return parsePcdPathAsync(pcdPath)
        .then(({ fileName, dirPath }) => {
          const status = get().cacheStatus[dirPath]?.[fileName];

          if (status) {
            return Promise.resolve(status);
          }

          return appWorker
            .request('idb', 'get', {
              key: fileName,
              storeName: `${tenantID}_${dirPath}_status`,
            })
            .then(data => {
              if (!data) {
                return {
                  isLoading: false,
                  loaded: false,
                  optimized: false,
                  size: 0,
                };
              }

              const pcdData = data as PCDData;
              const size = pcdData?.pointList?.byteLength ?? 0;

              const status: PcdFileCacheStatus = {
                isLoading: false,
                loaded: true,
                optimized: false,
                size,
              };

              const cacheStatus = get().cacheStatus;

              set({
                cacheStatus: {
                  ...cacheStatus,
                  [dirPath]: {
                    ...cacheStatus[dirPath],
                    [fileName]: status,
                  },
                },
              });

              return status;
            });
        })
        .catch(error => {
          console.group('[usePcdCacheStore.getPcdStatus] Error');
          console.warn(error);
          console.groupEnd();

          return Promise.resolve({
            isLoading: false,
            loaded: false,
            optimized: false,
            size: 0,
          });
        });
    },

    getPcdStatusMap(pcdDirPath) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      const statusMap = get().cacheStatus[pcdDirPath];

      if (statusMap != null && !isEqual(statusMap, {}))
        return Promise.resolve(statusMap);

      return get()
        .getPcdPathMap(pcdDirPath)
        .then(
          async pcdPathMap =>
            [
              pcdPathMap,
              (await appWorker.request('idb', 'getList', {
                keys: Object.values(pcdPathMap),
                storeName: `${tenantID}_${pcdDirPath}_status`,
              })) as PcdFileCacheStatus[],
            ] as const,
        )
        .then(([keyList, statusList]) => {
          const statusMap = statusList.reduce(
            (acc, status, index) => ({
              ...acc,
              [keyList[index]]: status ?? {
                isLoading: false,
                loaded: false,
                size: 0,
              },
            }),
            {} as {
              [pcdFileName: string]: PcdFileCacheStatus;
            },
          );

          set({
            cacheStatus: {
              ...get().cacheStatus,
              [pcdDirPath]: statusMap,
            },
          });

          return statusMap;
        })
        .catch(error => {
          console.group('[usePcdCacheStore.getPcdStatusMap] Error');
          console.warn(error);
          console.groupEnd();

          return {};
        });
    },

    removePcd(pcdPath) {
      return get().removePcdList([pcdPath]);
    },

    removePcdList(pcdPathList) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      const [path] = pcdPathList;

      return parsePcdPathAsync(path)
        .then(({ dirPath }) => {
          const statusEntries = pcdPathList.map(p => [
            p.replace(`${dirPath}/`, ''),
            { loaded: false, isLoading: false, size: 0 },
          ]) as [string, PcdFileCacheStatus][];

          get().setPcdStatusMap(dirPath, statusEntries);

          appWorker.request('idb', 'delList', {
            keys: pcdPathList.map(p => p.replace(`${dirPath}/`, '')),
            storeName: `${tenantID}_${dirPath}_cached`,
          });
        })
        .catch(error => {
          console.group('[usePcdCacheStore.removePcdList] Error');
          console.warn(error);
          console.groupEnd();

          return Promise.resolve();
        });
    },

    setPcd(pcdPath, data) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      return parsePcdPathAsync(pcdPath)
        .then(({ fileName, dirPath }) =>
          appWorker.request('idb', 'set', {
            key: fileName,
            value: data,
            storeName: `${tenantID}_${dirPath}_cached`,
          }),
        )
        .catch(error => {
          console.group('[usePcdCacheStore.setPcd] Error');
          console.warn(error);
          console.groupEnd();

          return Promise.resolve();
        });
    },

    setPcdList(entries) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      const [[pcdPath]] = entries;

      return parsePcdPathAsync(pcdPath)
        .then(({ dirPath }) =>
          appWorker
            .request('idb', 'setList', {
              entries: entries.map(
                ([p, pd]) =>
                  [p.replace(`${dirPath}/`, ''), pd] as [string, PCDData],
              ),
              storeName: `${tenantID}_${dirPath}_cached`,
            })
            .then(() => {}),
        )
        .catch(error => {
          console.group('[usePcdCacheStore.setPcdList] Error');
          console.warn(error);
          console.groupEnd();

          return Promise.resolve();
        });
    },

    setPcdPathMap(pcdDirPath, pcdNameMap) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      return appWorker
        .request('idb', 'set', {
          key: 'value',
          value: pcdNameMap,
          storeName: `${tenantID}_${pcdDirPath}_list`,
        })
        .then(() => {
          set({
            cachedPcdMapByPath: {
              ...get().cachedPcdMapByPath,
              [pcdDirPath]: pcdNameMap,
            },
          });
        })
        .catch(error => {
          console.group('[usePcdCacheStore.setPcdPathList] Error');
          console.warn(error);
          console.groupEnd();
        });
    },

    setPcdStatus(pcdDirPath, pcdPath, status) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      return parsePcdPathAsync(pcdPath)
        .then(({ fileName }) => {
          const cacheStatus = get().cacheStatus;

          set({
            cacheStatus: {
              ...cacheStatus,
              [pcdDirPath]: {
                ...cacheStatus[pcdDirPath],
                [fileName]: status,
              },
            },
          });

          return appWorker.request('idb', 'set', {
            key: fileName,
            storeName: `${tenantID}_${pcdDirPath}_status`,
            value: status,
          });
        })
        .catch(error => {
          console.group('[usePcdCacheStore.setPcdStatus] Error');
          console.warn(error);
          console.groupEnd();

          return Promise.resolve();
        });
    },

    setPcdStatusMap(pcdDirPath, entries, option) {
      const tenantID = assert();

      if (tenantID instanceof Error) return Promise.reject(tenantID);

      const appended = option?.appended ?? true;
      const statusMap = appended ? get().cacheStatus[pcdDirPath] : {};
      const newEntries = entries.map(([p, s]) => {
        const path = p.replace(`${pcdDirPath}/`, '');

        statusMap[path] = s;

        return [path, s];
      }) as [string, PcdFileCacheStatus][];

      set({
        cacheStatus: {
          ...get().cacheStatus,
          [pcdDirPath]: statusMap,
        },
      });

      return appWorker
        .request('idb', 'setList', {
          entries: newEntries,
          storeName: `${tenantID}_${pcdDirPath}_status`,
        })
        .then(() => {})
        .catch(error => {
          console.group('[usePcdCacheStore.setPcdStatusMap] Error');
          console.warn(error);
          console.groupEnd();

          return Promise.resolve();
        });
    },
  };
}, shallow);

export default usePcdCacheStore;
